import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import ChartContainer from '../charts/ChartContainer';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

const HandledAbandonedChart = ({ data, filters }) => {
  const getDateStr = (date) =>
    date instanceof Date ? date.toLocaleDateString('en-CA') : new Date(date).toLocaleDateString('en-CA');

  const filtered = data.filter((d) => {
    const matchCustomer = !filters.customer || d.Customer === filters.customer;
    const matchAgent = !filters.agent || d.Agent === filters.agent;
    const matchDate = !filters.selectedDate || getDateStr(d.Date) === filters.selectedDate;
    return matchCustomer && matchAgent && matchDate;
  });

  const grouped = {};
  const segments = {};
  let totalHandled = 0;
  let totals = {
    noInteraction: 0,
    duringRing: 0,
    afterInteraction: 0,
  };

  filtered.forEach((d) => {
    const dateStr = getDateStr(d.Date);
    const segmentId = d.Segment;

    if (!grouped[dateStr]) {
      grouped[dateStr] = {
        Handled: 0,
        Abandoned_NoInteraction: 0,
        Abandoned_DuringRing: 0,
        Abandoned_AfterInteraction: 0,
      };
    }

    grouped[dateStr].Handled += d.Handled;
    totalHandled += d.Handled;

    if (!segments[segmentId]) {
      segments[segmentId] = {
        hadMissedOrRejected: false,
        hadAbandoned: false,
        hadRing: false,
        date: d.Date,
      };
    }

    const validMissed = d.Missed === 1 && d.RingTime >= 20;
    if (d.Rejected === 1 || validMissed) {
      segments[segmentId].hadMissedOrRejected = true;
    }
    if (d.RingTime > 0) {
      segments[segmentId].hadRing = true;
    }
    if (d.Abandoned === 1) {
      segments[segmentId].hadAbandoned = true;
    }
  });

  const seen = new Set();
  Object.entries(segments).forEach(([segmentId, seg]) => {
    if (!seg.hadAbandoned || seen.has(segmentId)) return;

    const dateStr = getDateStr(seg.date);

    if (!grouped[dateStr]) {
      grouped[dateStr] = {
        Handled: 0,
        Abandoned_NoInteraction: 0,
        Abandoned_DuringRing: 0,
        Abandoned_AfterInteraction: 0,
      };
    }

    if (seg.hadMissedOrRejected) {
      grouped[dateStr].Abandoned_AfterInteraction += 1;
      totals.afterInteraction += 1;
    } else if (seg.hadRing) {
      grouped[dateStr].Abandoned_DuringRing += 1;
      totals.duringRing += 1;
    } else {
      grouped[dateStr].Abandoned_NoInteraction += 1;
      totals.noInteraction += 1;
    }

    seen.add(segmentId);
  });

  const labels = Object.keys(grouped).sort();

  // Move handledPercentages calculation AFTER grouped and labels are defined
  const handledPercentages = labels.map((date) => {
    const g = grouped[date];
    const total =
      g.Handled +
      g.Abandoned_NoInteraction +
      g.Abandoned_DuringRing +
      g.Abandoned_AfterInteraction;
    if (total === 0) return null;
    return Math.round((g.Handled / total) * 100);
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Handled',
        backgroundColor: '#4caf50',
        data: labels.map((date) => grouped[date].Handled),
        stack: 'handled',
        datalabels: {
          display: (ctx) => handledPercentages[ctx.dataIndex] !== null,
          anchor: 'center',
          align: 'center',
          formatter: (value, ctx) => `${handledPercentages[ctx.dataIndex]}%`,
          color: 'white',
          font: { weight: 'bold', size: 14 },
          clamp: true,
          clip: true,
        },
      },
      {
        label: 'Abandoned - Geen interactie',
        backgroundColor: '#e0e0e0',
        data: labels.map((date) => grouped[date].Abandoned_NoInteraction),
        stack: 'abandoned',
        datalabels: { display: false },
      },
      {
        label: 'Abandoned - Gedurende beltoon',
        backgroundColor: '#ffc107',
        data: labels.map((date) => grouped[date].Abandoned_DuringRing),
        stack: 'abandoned',
        datalabels: { display: false },
      },
      {
        label: 'Abandoned - Na gemiste/geweigerde oproep',
        backgroundColor: '#f44336',
        data: labels.map((date) => grouped[date].Abandoned_AfterInteraction),
        stack: 'abandoned',
        datalabels: { display: false },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const date = context.label; // the label (date) for this tooltip item
            const value = context.parsed.y ?? context.parsed; // value for this dataset at this label

            // Get grouped data for the current date
            const g = grouped[date];

            if (!g) return `${label}: ${value}`; // fallback

            // Calculate total for this date
            const total = 
              g.Handled +
              g.Abandoned_NoInteraction +
              g.Abandoned_DuringRing +
              g.Abandoned_AfterInteraction;

            if (total === 0) return `${label}: ${value}`;

            // Calculate percentage
            const percent = ((value / total) * 100).toFixed(1);

            return `${label}: ${value} (${percent}%)`;
          }
        }
      },
      datalabels: {},
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  return (
    <div style={{ width: '50%' }}>
      <div
        style={{
          backgroundColor: 'white',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'solid 0.2rem #e6f2f6',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        <h3>
          Totale oproepen per dag{' '}
          {filters.selectedDate
            ? `(${filters.selectedDate})`
            : `( Laatste ${labels.length} dagen )`}
        </h3>
        <div style={{ fontSize: '0.95rem', color: '#555' }}>
          <strong>Handled:</strong> {totalHandled} &nbsp;|&nbsp;
          <strong>Abandoned:</strong>{' '}
          {totals.afterInteraction + totals.duringRing + totals.noInteraction}
        </div>
      </div>

      <ChartContainer size="small">
        <Bar data={chartData} options={options} />
      </ChartContainer>
    </div>
  );
};

export default HandledAbandonedChart;
